// Activity Session Manager:
// Each category (Quizz, Mots-Croisés, SMS, Contes/Parchemins) has its own independent active session.
// Starting a new item in a category replaces the previous unfinished session of THAT category,
// but does NOT wipe out active sessions in other categories/tabs (cumul inter-onglets autorisé).

export interface ActiveSmsSession {
  discussionId: string;
  playerRole: 'player' | 'contact';
}

export interface ActiveScrollSession {
  title: string;
  page: number;
  sanctuaryName?: string;
}

// -------------------------------------------------------------
// 1. QUIZ CHALLENGES
// -------------------------------------------------------------
export function getActiveQuizId(): string | null {
  try {
    return localStorage.getItem('tamazight_active_quiz_session');
  } catch {
    return null;
  }
}

export function setActiveQuizId(challengeId: string): void {
  try {
    const currentId = getActiveQuizId();
    if (currentId && currentId !== challengeId) {
      localStorage.removeItem('tamazight_active_quiz_state_' + currentId);
    }
    localStorage.setItem('tamazight_active_quiz_session', challengeId);
    window.dispatchEvent(new CustomEvent('tamazight_active_quiz_changed', { detail: challengeId }));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (e) {
    console.error('Failed to set active quiz session:', e);
  }
}

export function clearActiveQuizId(challengeId?: string): void {
  try {
    const currentId = getActiveQuizId();
    if (!challengeId || currentId === challengeId) {
      if (currentId) {
        localStorage.removeItem('tamazight_active_quiz_state_' + currentId);
      }
      localStorage.removeItem('tamazight_active_quiz_session');
      window.dispatchEvent(new CustomEvent('tamazight_active_quiz_changed', { detail: null }));
      window.dispatchEvent(new CustomEvent('storage'));
    }
  } catch (e) {
    console.error('Failed to clear active quiz session:', e);
  }
}

// -------------------------------------------------------------
// 2. CROSSWORD CHALLENGES
// -------------------------------------------------------------
export function getActiveCrosswordId(): string | null {
  try {
    return localStorage.getItem('tamazight_active_crossword_session');
  } catch {
    return null;
  }
}

export function setActiveCrosswordId(challengeId: string): void {
  try {
    localStorage.setItem('tamazight_active_crossword_session', challengeId);
    window.dispatchEvent(new CustomEvent('tamazight_active_crossword_changed', { detail: challengeId }));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (e) {
    console.error('Failed to set active crossword session:', e);
  }
}

export function clearActiveCrosswordId(challengeId?: string): void {
  try {
    const currentId = getActiveCrosswordId();
    if (!challengeId || currentId === challengeId) {
      localStorage.removeItem('tamazight_active_crossword_session');
      window.dispatchEvent(new CustomEvent('tamazight_active_crossword_changed', { detail: null }));
      window.dispatchEvent(new CustomEvent('storage'));
    }
  } catch (e) {
    console.error('Failed to clear active crossword session:', e);
  }
}

// -------------------------------------------------------------
// 3. SMS DISCUSSIONS
// -------------------------------------------------------------
export function getActiveSmsSession(): ActiveSmsSession | null {
  try {
    const raw = localStorage.getItem('tamazight_active_sms_session');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setActiveSmsSession(discussionId: string, playerRole: 'player' | 'contact' = 'player'): void {
  try {
    const current = getActiveSmsSession();
    if (current && current.discussionId !== discussionId) {
      localStorage.removeItem('tamazight_active_sms_state');
    }
    const data: ActiveSmsSession = { discussionId, playerRole };
    localStorage.setItem('tamazight_active_sms_session', JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('tamazight_active_sms_changed', { detail: data }));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (e) {
    console.error('Failed to set active SMS session:', e);
  }
}

export function clearActiveSmsSession(discussionId?: string): void {
  try {
    const current = getActiveSmsSession();
    if (!discussionId || current?.discussionId === discussionId) {
      localStorage.removeItem('tamazight_active_sms_session');
      localStorage.removeItem('tamazight_active_sms_state');
      window.dispatchEvent(new CustomEvent('tamazight_active_sms_changed', { detail: null }));
      window.dispatchEvent(new CustomEvent('storage'));
    }
  } catch (e) {
    console.error('Failed to clear active SMS session:', e);
  }
}

// -------------------------------------------------------------
// 4. CONTES / PARCHEMINS (SCROLLS)
// -------------------------------------------------------------
export function getActiveScrollSession(): ActiveScrollSession | null {
  try {
    const raw = localStorage.getItem('tamazight_active_scroll_session');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setActiveScrollSession(title: string, page: number = 0, sanctuaryName?: string): void {
  try {
    const current = getActiveScrollSession();
    if (current && current.title !== title) {
      localStorage.removeItem('tamazight_active_scroll_' + current.title);
    }
    const data: ActiveScrollSession = { title, page, sanctuaryName };
    localStorage.setItem('tamazight_active_scroll_session', JSON.stringify(data));
    localStorage.setItem('tamazight_active_scroll_' + title, page.toString());
    window.dispatchEvent(new CustomEvent('tamazight_active_scroll_changed', { detail: data }));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (e) {
    console.error('Failed to set active scroll session:', e);
  }
}

export function clearActiveScrollSession(title?: string): void {
  try {
    const current = getActiveScrollSession();
    if (!title || current?.title === title) {
      if (current) {
        localStorage.removeItem('tamazight_active_scroll_' + current.title);
      }
      if (title) {
        localStorage.removeItem('tamazight_active_scroll_' + title);
      }
      localStorage.removeItem('tamazight_active_scroll_session');
      window.dispatchEvent(new CustomEvent('tamazight_active_scroll_changed', { detail: null }));
      window.dispatchEvent(new CustomEvent('storage'));
    }
  } catch (e) {
    console.error('Failed to clear active scroll session:', e);
  }
}
